/**
 * Enhanced SearchResultsPanel with Date-Aware Filtering
 * Shows filtered providers count when date filter is active
 */

import React, { useMemo } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { ListingCard, PaginationLinks } from '../../../components';
import ListingCardSkeleton from '../../../components/ListingCard/ListingCardSkeleton';
import { useDateFilteredSearch } from '../../../hooks/useDateFilteredSearch';

import css from './SearchResultsPanel.module.css';

/**
 * Enhanced SearchResultsPanel component with date-aware filtering
 */
const SearchResultsPanelEnhanced = props => {
  const {
    className,
    rootClassName,
    listings = [],
    pagination,
    search,
    setActiveListing,
    isMapVariant = true,
    listingTypeParam,
    searchInProgress = false,
    listingsAreLoaded = true,
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const pageName = listingTypeParam ? 'SearchPageWithListingType' : 'SearchPage';

  // Extract date from search params for filtering
  const searchDateString = search?.dates;
  
  // Ensure listings is an array
  const safeListings = Array.isArray(listings) ? listings : [];

  // Use date-filtered search hook if we have a date filter
  const {
    filteredListings,
    isFiltering,
    filteredOutCount,
    error: filterError
  } = useDateFilteredSearch(
    searchDateString ? safeListings : [], // Only filter if we have a date
    searchDateString
  );

  // Use filtered listings if we have a date filter, otherwise use original
  const displayListings = searchDateString ? (Array.isArray(filteredListings) ? filteredListings : []) : safeListings;
  const showFilterMessage = searchDateString && !isFiltering && filteredOutCount > 0;

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={pageName}
        pagePathParams={{ listingType: listingTypeParam }}
        pageSearchParams={search}
        pagination={pagination}
      />
    ) : null;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 767px) 100vw',
        `(max-width: 1023px) ${panelMediumWidth}vw`,
        `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
        `${panelLargeWidth / 3}vw`,
      ].join(', ');
    } else {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 549px) 100vw',
        '(max-width: 767px) 50vw',
        `(max-width: 1439px) 26vw`,
        `(max-width: 1920px) 18vw`,
        `14vw`,
      ].join(', ');
    }
  };

  // Show skeleton loading state when search is in progress or listings aren't loaded
  const showSkeletonLoading = searchInProgress || !listingsAreLoaded || isFiltering;

  // Generate skeleton cards for loading state
  const renderSkeletonCards = () => {
    // Show 6 skeleton cards as a reasonable placeholder count
    const skeletonCount = 6;
    return Array.from({ length: skeletonCount }, (_, index) => (
      <ListingCardSkeleton
        key={`skeleton-${index}`}
        className={css.listingCard}
      />
    ));
  };

  return (
    <div className={classes}>
      {/* Show date filter message */}
      {showFilterMessage && (
        <div className={css.dateFilterMessage}>
          <FormattedMessage
            id="SearchResultsPanel.dateFilterMessage"
            values={{ count: filteredOutCount }}
            defaultMessage="Filtered out {count} provider{count, plural, one {} other{s}} booked on selected date"
          />
        </div>
      )}

      {/* Show filter error if any */}
      {filterError && (
        <div className={css.filterError}>
          <FormattedMessage
            id="SearchResultsPanel.dateFilterError"
            defaultMessage="Unable to check availability for some providers"
          />
        </div>
      )}

      <div className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
        {showSkeletonLoading ? (
          renderSkeletonCards()
        ) : (
          Array.isArray(displayListings) && displayListings.length > 0 ? (
            displayListings.map(l => (
              <ListingCard
                className={css.listingCard}
                key={l.id.uuid}
                listing={l}
                renderSizes={cardRenderSizes(isMapVariant)}
                setActiveListing={setActiveListing}
              />
            ))
          ) : (
            <div>No listings available</div>
          )
        )}
        {props.children}
      </div>
      {paginationLinks}
    </div>
  );
};

export default SearchResultsPanelEnhanced;
